"use client"

import { Navigation } from "@/components/navigation"
import { Footer } from "@/components/footer"
import { useEffect, useState, useRef } from "react"
import { Button } from "@/components/ui/button"
import { Download, ExternalLink } from "lucide-react"
import Link from "next/link"

export default function APIDocsPage() {
  const [mounted, setMounted] = useState(false)
  const [isLoading, setIsLoading] = useState(true)
  const scalarRef = useRef<HTMLDivElement>(null)

  useEffect(() => {
    setMounted(true)
  }, [])

  useEffect(() => {
    if (!mounted) return

    const loadScalar = async () => {
      try {
        setIsLoading(true)
        
        // Clear any existing content
        if (scalarRef.current) {
          scalarRef.current.innerHTML = ''
        }
        
        // Create the script element with data attributes (Scalar's official CDN method)
        const apiRefScript = document.createElement('script')
        apiRefScript.id = 'api-reference'
        apiRefScript.setAttribute('data-url', '/api/openapi')
        apiRefScript.setAttribute('data-layout', 'modern')
        apiRefScript.setAttribute('data-show-sidebar', 'true')
        apiRefScript.setAttribute('data-hide-models', 'false')
        apiRefScript.setAttribute('data-hide-download-button', 'false')
        
        // Add the script to our container
        if (scalarRef.current) {
          scalarRef.current.appendChild(apiRefScript)
        }
        
        // Load the Scalar CDN script
        const cdnScript = document.createElement('script')
        cdnScript.src = 'https://cdn.jsdelivr.net/npm/@scalar/api-reference@latest'
        cdnScript.async = true
        
        cdnScript.onload = () => {
          setTimeout(() => {
            setIsLoading(false)
          }, 1000)
        }
        
        cdnScript.onerror = () => {
          console.error('Failed to load Scalar API Reference script')
          setIsLoading(false)
        }
        
        document.head.appendChild(cdnScript)
        
      } catch (error) {
        console.error('Failed to load Scalar API Reference:', error)
        setIsLoading(false)
      }
    }

    loadScalar()
  }, [mounted])

  if (!mounted) {
    return (
      <div className="min-h-screen flex flex-col">
        <Navigation />
        <div className="flex-1 flex items-center justify-center">
          <div className="text-center">
            <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
            <p className="text-muted-foreground">Loading...</p>
          </div>
        </div>
        <Footer />
      </div>
    )
  }

  return (
    <div className="min-h-screen flex flex-col">
      <Navigation />
      
      {/* API Docs Header */}
      <div className="border-b bg-background px-6 py-6">
        <div className="max-w-7xl mx-auto">
          <div className="flex items-center justify-between">
            <div>
              <h1 className="text-3xl font-bold">API Documentation</h1>
              <p className="mt-2 text-muted-foreground">
                Comprehensive API reference for the Locsy Service
              </p>
            </div>
            <div className="flex space-x-4">
              <Button variant="outline" asChild>
                <Link href="/openapi.json" target="_blank" rel="noopener noreferrer">
                  <Download className="w-4 h-4 mr-2" />
                  OpenAPI Spec
                </Link>
              </Button>
              <Button asChild>
                <Link href="/dashboard">
                  <ExternalLink className="w-4 h-4 mr-2" />
                  Dashboard
                </Link>
              </Button>
            </div>
          </div>
        </div>
      </div>

      {/* Scalar API Reference Container */}
      <div className="flex-1">
        {isLoading && (
          <div className="flex items-center justify-center min-h-[400px]">
            <div className="text-center">
              <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
              <p className="text-muted-foreground">Loading API Documentation...</p>
            </div>
          </div>
        )}
        <div 
          ref={scalarRef}
          className="w-full min-h-[600px]"
          style={{ display: isLoading ? 'none' : 'block' }}
        />
      </div>

      <Footer />
    </div>
  )
}